import {
  FIREBASE_CLIENT_BY_PROJECT_ID,
  type FirebaseWebClientConfig,
} from "../../functions/src/shared/firebaseClient";
import { LANEFUL_DOMAIN_BY_PROJECT_ID } from "../../functions/src/shared/lanefulDomain";

interface EnvironmentConfig {
  project_id: string;
  trpc_url: string;
  laneful_api_url: string;
  laneful_domain: string;
  linkedinClientId: string;
  // The LinkedIn *posting* app's client id (#681, `w_member_social` scope) —
  // a different OAuth app/consumer than `linkedinClientId` above, which is
  // sign-in-only. Empty until `#683` (human-task: register the app, get it
  // through LinkedIn's review) provisions a real value — same "code ships
  // now, value lands at deploy time" shape as the server's
  // `LINKEDIN_POSTING_CLIENT_ID` secret (DECISIONS.md D-681-1). A client id
  // is a public identifier, not a secret, so it's safe to bake into this
  // file the same way `linkedinClientId` already is.
  linkedinPostingClientId: string;
  requireLinkedinVerification: boolean;
  // Integrations release flag (#719) — social syndication (X, LinkedIn, Bluesky,
  // Mastodon, Hashnode, dev.to) shipped behind this flag. Narrows the
  // Integrations page's third-party rows and the post editor's Distribution
  // UI's external-syndication controls — never the nav item or the
  // /integrations route, which always render (#1020: the route now also
  // carries the built-in newsletter). The unset value reads as OFF (D-719-4)
  // and only VITE_INTEGRATIONS_ENABLED=true turns it on — which
  // apphosting.dev.yaml and apphosting.prod.yaml both now set (#1044), so the
  // deployed state in every environment is ON. Custom VITE_* keys land on
  // `ImportMetaEnv`'s `any` index signature, so narrow at the call site the
  // way `src/lib/gtm.ts` does.
  integrationsEnabled: boolean;
  /**
   * Series + Co-authors authoring flag — the three Advanced-options controls
   * it gates (Series, Position in series, Co-authors) are hidden while the
   * feature is only half-there (product call, 2026-08-21).
   *
   * They are NOT unbuilt: the columns exist (`series_name`, `series_position`,
   * `co_author_user_ids`), the server validates and prunes them, and the blog
   * renders both — `BlogPostArticle` prints "Part N of the X series" and
   * `resolveCoAuthorEmails` resolves the ids. What is missing is everything
   * around them: no series index, no way to reorder, no author pages, and a
   * series of one renders nothing at all. Offering the inputs invites an
   * author to fill in metadata that mostly goes nowhere.
   *
   * TEMPORARY, not a retirement. The removal condition is the rest of the
   * feature landing: a series index a reader can walk, a way to order the
   * posts in one, and author pages for a co-author byline to point at. When
   * that ships, turn the flag on (or delete it and this comment with it) —
   * nothing else has to change. Until then an author who ALREADY has a stored
   * series or co-authors keeps them, keeps seeing them rendered on the blog,
   * and has no UI to edit or remove them. That is accepted deliberately:
   * hiding an input is reversible, clearing the data behind it is not.
   *
   * Same shape and the same default as `integrationsEnabled` above, for the
   * same reason: OFF everywhere until someone sets
   * VITE_SERIES_AND_CO_AUTHORS_ENABLED=true, and no other change needed to
   * bring the fields back — the editor keeps the state either way.
   */
  seriesAndCoAuthorsEnabled: boolean;
  limits: {
    maxDomainsPerWorkspace: number;
    maxEmailsPerDomain: number;
  };
  approvalCooldownDays: number;
  firebase: FirebaseWebClientConfig;
}

// #719 — both environments read the same knob; hoisted once rather than
// repeated per-environment so there's exactly one call site to narrow the
// `any` index signature at (same reasoning as `src/lib/gtm.ts`), and no risk
// of the two copies drifting.
const integrationsEnabled: boolean =
  (import.meta.env.VITE_INTEGRATIONS_ENABLED as string | undefined) === "true";

// Hoisted for the same reason as the line above — one place to narrow the
// `any` index signature, and no two copies to drift.
const seriesAndCoAuthorsEnabled: boolean =
  (import.meta.env.VITE_SERIES_AND_CO_AUTHORS_ENABLED as string | undefined) ===
  "true";

const environments: Record<string, EnvironmentConfig> = {
  production: {
    project_id: "goodsender-prod",
    trpc_url: "https://us-central1-goodsender-prod.cloudfunctions.net",
    laneful_api_url: "https://api.laneful.net/",
    laneful_domain: LANEFUL_DOMAIN_BY_PROJECT_ID["goodsender-prod"],
    linkedinClientId: "86hawnjw6hfs74",
    linkedinPostingClientId: "86hawnjw6hfs74",
    requireLinkedinVerification: true,
    integrationsEnabled,
    seriesAndCoAuthorsEnabled,
    limits: {
      maxDomainsPerWorkspace: 5,
      maxEmailsPerDomain: 250000,
    },
    approvalCooldownDays: 14,
    firebase: FIREBASE_CLIENT_BY_PROJECT_ID["goodsender-prod"],
  },
  development: {
    project_id: "goodsender-dev",
    laneful_api_url: "https://api.dev.laneful.net/",
    laneful_domain: LANEFUL_DOMAIN_BY_PROJECT_ID["goodsender-dev"],
    linkedinClientId: "77qwcpvj2sydhh",
    linkedinPostingClientId: "77qwcpvj2sydhh",
    requireLinkedinVerification: false,
    integrationsEnabled,
    seriesAndCoAuthorsEnabled,
    // VITE_TRPC_URL (web .env.local) lets local dev bypass the functions
    // emulator and talk to the deployed dev functions (real dev DB); pair it
    // with VITE_USE_AUTH_EMULATOR=false so real ID tokens are sent.
    // Custom VITE_* keys land on `ImportMetaEnv`'s `any` index signature, so
    // narrow at the call site the way `src/lib/gtm.ts` does.
    trpc_url:
      (import.meta.env.VITE_TRPC_URL as string | undefined) ||
      (import.meta.env.DEV
        ? "http://localhost:5001/goodsender-dev/us-central1"
        : "https://us-central1-goodsender-dev.cloudfunctions.net"),
    limits: {
      maxDomainsPerWorkspace: 2,
      maxEmailsPerDomain: 200,
    },
    approvalCooldownDays: 14,
    firebase: FIREBASE_CLIENT_BY_PROJECT_ID["goodsender-dev"],
  },
};

const getCurrentEnvironment = (): string => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const env =
    import.meta.env.VITE_ENVIRONMENT ||
    import.meta.env.VITE_MODE ||
    import.meta.env.MODE;

  switch (env) {
    case "production":
      return "production";
    default:
      return "development";
  }
};

const currentEnv = getCurrentEnvironment();
const Config = environments[currentEnv];

if (!Config) {
  throw new Error(`Environment configuration not found for: ${currentEnv}`);
}

export default Config;
export { environments, getCurrentEnvironment };

export const isProduction = currentEnv === "production";
